import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';

import { cn } from '../lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva(
  // Diameter derives from the density control heights (same knob button.tsx
  // rides), exposed as --radio-size so the dot indicator scales in lockstep.
  //
  // WCAG 2.5.8 AA (Target Size, Minimum) — floored at 24 CSS px for the same
  // reason as `checkbox.tsx`: Radix renders the control as a real
  // <button role="radio">, so the drawn circle IS the pointer target and there
  // is no larger hit area behind it. Un-floored, `comfortable` produced
  // sm=14px / default=16px and `compact` produced 10.5px / 12px.
  'aspect-square rounded-full border border-primary text-primary ring-offset-background size-[var(--radio-size)] [&_svg]:size-[calc(var(--radio-size)*0.625)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: '[--radio-size:max(calc(var(--control-height-sm)*0.4375),24px)]',
        default: '[--radio-size:max(calc(var(--control-height-sm)*0.5),24px)]',
        lg: '[--radio-size:max(calc(var(--control-height-md)*0.5),24px)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> &
    VariantProps<typeof radioGroupItemVariants>
>(({ className, size, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem, radioGroupItemVariants };
